import { useEffect, useState } from 'react';

import { router, usePage } from '@inertiajs/react';
import { Heart } from 'lucide-react';

import {
    WishlistCollectionOption,
    WishlistCollectionsDialog,
} from './WishlistCollectionsDialog';
import {
    Routes,
    wishlistItemCollectionsRoute,
    wishlistItemRoute,
} from '../../models/routes';
import { csrfTokenHeader } from '../../services/CsrfTokenService';
import { CurrentUser } from '../../types';
import { cn } from '../../utils';
import { Button } from '../ui/Button';

type PageProps = {
    currentUser: CurrentUser | null;
};

type Props = {
    productId: number;
    initialWishlisted: boolean;
    className?: string;
    showLabel?: boolean;
};

type CollectionsPayload = {
    wishlisted: boolean;
    collections: WishlistCollectionOption[];
};

const WISHLIST_UPDATED_EVENT = 'carton:wishlist-updated';

const responseError = async (response: Response) => {
    const payload = await response.json().catch(() => null);
    return payload?.errors
        ? Object.values(payload.errors).join(' ')
        : 'Не вдалося оновити список бажаного';
};

const removeFromWishlist = async (productId: number) => {
    const response = await fetch(wishlistItemRoute(productId), {
        method: 'DELETE',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...csrfTokenHeader(),
        },
    });

    if (!response.ok) throw new Error(await responseError(response));
};

const addToWishlist = async (productId: number, collectionIds: number[]) => {
    const response = await fetch(Routes.WishlistItems, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            ...csrfTokenHeader(),
        },
        body: JSON.stringify({
            product_id: productId,
            collection_ids: collectionIds,
        }),
    });

    if (!response.ok) throw new Error(await responseError(response));
};

export const WishlistButton = ({
    productId,
    initialWishlisted,
    className,
    showLabel = false,
}: Props) => {
    const { currentUser } = usePage<PageProps>().props;
    const [wishlisted, setWishlisted] = useState(initialWishlisted);
    const [loading, setLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [collections, setCollections] = useState<WishlistCollectionOption[]>(
        [],
    );
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        setWishlisted(initialWishlisted);

        const syncWishlistState = (event: Event) => {
            const detail = (
                event as CustomEvent<{
                    productId: number;
                    wishlisted: boolean;
                }>
            ).detail;

            if (detail.productId === productId) {
                setWishlisted(detail.wishlisted);
            }
        };

        window.addEventListener(WISHLIST_UPDATED_EVENT, syncWishlistState);
        return () =>
            window.removeEventListener(
                WISHLIST_UPDATED_EVENT,
                syncWishlistState,
            );
    }, [initialWishlisted, productId]);

    const publishWishlistState = (nextWishlisted: boolean) => {
        window.dispatchEvent(
            new CustomEvent(WISHLIST_UPDATED_EVENT, {
                detail: { productId, wishlisted: nextWishlisted },
            }),
        );
    };

    const loadCollections = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch(
                wishlistItemCollectionsRoute(productId),
                { headers: { Accept: 'application/json' } },
            );
            if (!response.ok) throw new Error(await responseError(response));

            const payload = (await response.json()) as CollectionsPayload;
            setCollections(payload.collections);
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleDialogOpenChange = (open: boolean) => {
        setDialogOpen(open);
        if (!open) setError(null);
    };

    const updateCollection = (collectionId: number, selected: boolean) => {
        setCollections((current) =>
            current.map((collection) =>
                collection.id === collectionId
                    ? { ...collection, selected }
                    : collection,
            ),
        );
    };

    const selectAll = () => {
        setCollections((current) =>
            current.map((collection) => ({ ...collection, selected: false })),
        );
    };

    const saveToWishlist = async () => {
        setLoading(true);
        setError(null);
        try {
            await addToWishlist(
                productId,
                collections
                    .filter((collection) => collection.selected)
                    .map((collection) => collection.id),
            );
            setDialogOpen(false);
            publishWishlistState(true);
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const handleButtonClick = async () => {
        if (!currentUser) {
            router.get(Routes.SignIn, {
                return_to: `${window.location.pathname}${window.location.search}`,
            });
            return;
        }

        if (!wishlisted) {
            setDialogOpen(true);
            void loadCollections();
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await removeFromWishlist(productId);
            publishWishlistState(false);

            if (window.location.pathname === Routes.Wishlist) {
                router.reload({
                    only: ['products', 'collections', 'nextPage', 'accountNav'],
                });
            }
        } catch (requestError) {
            setError((requestError as Error).message);
        } finally {
            setLoading(false);
        }
    };

    const label = wishlisted
        ? 'Прибрати товар зі списку бажаного'
        : 'Додати товар до списку бажаного';

    return (
        <>
            <Button
                type="button"
                variant="outline"
                size={showLabel ? 'default' : 'icon'}
                className={cn(
                    'gap-2 shadow-sm',
                    showLabel
                        ? 'bg-background/90 backdrop-blur-sm'
                        : 'border-0 bg-transparent text-white group-hover:bg-white group-hover:text-slate-900',
                    className,
                )}
                aria-label={label}
                aria-pressed={wishlisted}
                data-testid={`wishlist-button-${productId}`}
                onClick={handleButtonClick}
                disabled={loading}
                title={error || label}
            >
                <Heart
                    className={cn(
                        'h-4 w-4 drop-shadow-sm',
                        wishlisted && 'fill-red-500 text-red-500',
                    )}
                />
                {showLabel &&
                    (wishlisted ? 'У списку бажаного' : 'До списку бажаного')}
            </Button>

            <WishlistCollectionsDialog
                open={dialogOpen}
                onOpenChange={handleDialogOpenChange}
                title="Додати до списку бажаного"
                description="Товар завжди буде в загальному списку. Додатково можна обрати одну чи кілька колекцій."
                collections={collections}
                loading={loading}
                error={error}
                submitLabel="Додати"
                emptyMessage="Інших колекцій ще немає. Створити колекцію можна на сторінці списку бажаного."
                onSelectAll={selectAll}
                onCollectionChange={updateCollection}
                onSubmit={saveToWishlist}
            />
        </>
    );
};
